import { formatChartDateAxis } from '@/utils/charts/axisFormatters';
import CashFlowTable from '@/components/cashflow/CashFlowTable';
import { IconButtonStyledOutlined } from '@/components/common/iconButtonStyledOutlined';
import { PaperFrameStyled } from '@/components/common/paperFrameStyled';
import ViewModeBar from '@/components/common/ViewModeBar';
import { ViewMode } from '@/constants/enum';
import { CategoryAmount } from '@/interfaces/cashFlow';
import { BankTransaction } from '@/store/transactionsApi';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PrintIcon from '@mui/icons-material/Print';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useMemo, useRef, useState } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import { useGetCashflowCategoriesQuery } from '@/store/cashFlowApi';
import AddTransactionModal from './AddTransactionModal';
import CashFlowDetailsBarChart, {
  CashFlowTabMode,
  ChartRef,
} from './CashFlowDetailsBarChart';
import ManageRulesModal from './ManageRulesModal';
import { Capacitor } from '@capacitor/core';
import { ContentContainerVariant } from '@/widgets/contentContainer';
import {
  TabItem,
  TabsToMobileSelect,
  useTabsToMobileSelectPanel,
} from '@/components/common/tabs-to-mobile-select/TabsToMobileSelect';

interface CashFlowDetailsProps {
  incomeByCategory: Array<CategoryAmount>;
  spendingByCategory: Array<CategoryAmount>;
  topAmount: number;
  transactions: BankTransaction[];
  startDate: string;
  endDate: string;
  investorId?: number;
  firmDefaultView?: ViewMode;
  container?: ContentContainerVariant;
}

interface TitleProps {
  viewMode: ViewMode;
  chartRef: React.RefObject<ChartRef>;
  showMenuItems?: boolean;
}

interface ContentProps {
  transactions: BankTransaction[];
  startDate: string;
  endDate: string;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  chartRef: React.RefObject<ChartRef>;
}

function normalizeCashflowGroup(
  value: BankTransaction['CashflowGroup'] | null | undefined,
) {
  return value?.toLowerCase();
}

const CashFlowDetails: React.FC<CashFlowDetailsProps> = ({
  transactions,
  startDate,
  endDate,
  investorId,
  firmDefaultView,
  container = 'page',
}) => {
  // Initialize view mode with firm default if provided, otherwise default to Bar (chart)
  const [viewMode, setViewMode] = useState<ViewMode>(
    firmDefaultView ?? ViewMode.Bar,
  );
  const chartRef = useRef<ChartRef>(null);

  const menuEnabled = ['page', 'widget'];
  const showMenuItems = menuEnabled.some((page) => page === container);

  return (
    <PaperFrameStyled sx={{ flexGrow: 1, width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          padding: '24px',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '24px',
          alignSelf: 'stretch',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            alignSelf: 'stretch',
            width: '100%',
          }}
        >
          <Title
            viewMode={viewMode}
            chartRef={chartRef}
            investorId={investorId}
            showMenuItems={showMenuItems}
          />
        </Box>
      </Box>

      <Content
        transactions={transactions}
        startDate={startDate}
        endDate={endDate}
        viewMode={viewMode}
        setViewMode={setViewMode}
        chartRef={chartRef}
      />
    </PaperFrameStyled>
  );
};

const Title: React.FC<TitleProps & { investorId?: number }> = ({
  viewMode,
  chartRef,
  investorId,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [isAddTransactionModalOpen, setIsAddTransactionModalOpen] =
    useState(false);
  const [isManageRulesModalOpen, setIsManageRulesModalOpen] = useState(false); // State for Manage Rules modal
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null); // State for menu anchor
  const [initialModalTab, setInitialModalTab] = useState(0); // State for initial tab
  const openMenu = Boolean(anchorEl);

  const isNative = Capacitor.getPlatform() !== 'web';

  const handleOpenAddTransactionModal = (tabIndex = 0) => {
    setInitialModalTab(tabIndex); // Set the desired initial tab
    setIsAddTransactionModalOpen(true);
  };
  const handleCloseAddTransactionModal = () => {
    setIsAddTransactionModalOpen(false);
  };

  const handleOpenManageRulesModal = () => {
    setIsManageRulesModalOpen(true);
    handleCloseMenu(); // Close menu when modal opens
  };
  const handleCloseManageRulesModal = () => {
    setIsManageRulesModalOpen(false);
  };

  const handleClickMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // Placeholder handler for Upload Transactions
  const handleUploadTransactions = () => {
    handleOpenAddTransactionModal(1); // Open modal to tab 1 (Bulk Upload)
    handleCloseMenu();
  };

  const handlePrint = () => {
    chartRef.current?.exportAsPrint();
  };

  return (
    <>
      <Typography
        variant='h5'
        sx={{
          fontSize: { sm: '24px', xs: '18px' },
          fontWeight: 600,
          lineHeight: '133.4%',
        }}
      >
        {t('Cash Flow')}
      </Typography>

      <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 } }}>
        {isMobile ? (
          <IconButtonStyledOutlined
            variant='outlined'
            onClick={() => {
              handleOpenAddTransactionModal();
            }}
            color='primary'
          >
            <AddIcon />
          </IconButtonStyledOutlined>
        ) : (
          <Button
            variant='contained'
            startIcon={<AddIcon />}
            onClick={() => {
              handleOpenAddTransactionModal();
            }}
          >
            {t('cashFlow.addTransaction')}
          </Button>
        )}

        {/* Print Button - only show when in Bar view mode */}
        {viewMode === ViewMode.Bar && !isNative && (
          <>
            <IconButtonStyledOutlined
              variant='outlined'
              onClick={handlePrint}
            >
              <PrintIcon />
            </IconButtonStyledOutlined>
          </>
        )}
        {/* Options Menu Button */}
        <IconButtonStyledOutlined
          aria-label={t('cashFlow.moreOptions')}
          id='options-button'
          aria-controls={openMenu ? 'options-menu' : undefined}
          aria-expanded={openMenu ? 'true' : undefined}
          aria-haspopup='true'
          onClick={handleClickMenu}
          variant='outlined'
          size='small'
        >
          <MoreVertIcon />
        </IconButtonStyledOutlined>
        {/* Options Menu */}
        <Menu
          id='options-menu'
          slotProps={{
            list: {
              'aria-labelledby': 'options-button',
            },
          }}
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleCloseMenu}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleOpenManageRulesModal}>
            {t('cashFlow.manageRules.title')}
          </MenuItem>
          <MenuItem onClick={handleUploadTransactions}>
            {t('cashFlow.uploadTransactions')}
          </MenuItem>
        </Menu>
      </Box>

      <AddTransactionModal
        open={isAddTransactionModalOpen}
        onClose={handleCloseAddTransactionModal}
        initialTab={initialModalTab}
        investorId={investorId}
      />
      {/* Manage Rules Modal */}
      <ManageRulesModal
        open={isManageRulesModalOpen}
        onClose={handleCloseManageRulesModal}
        investorId={investorId}
      />
    </>
  );
};

const TAB_ORDER: CashFlowTabMode[] = ['both', 'income', 'spending'];

const Content: React.FC<ContentProps & { investorId?: number }> = ({
  transactions,
  startDate,
  endDate,
  viewMode,
  setViewMode,
  chartRef,
  investorId,
}) => {
  const { t } = useLocalization();
  // Used to display a Bulk Edit button when in Table view mode
  const [bulkEditContainer, setBulkEditContainer] =
    useState<HTMLDivElement | null>(null);

  const {
    instanceId,
    selectedTabKey,
    handleTabChange: setSelectedTab,
  } = useTabsToMobileSelectPanel();

  const tabs: TabItem[] = useMemo(
    () => [
      { label: t('both'), key: 'both' },
      { label: t('incomeDetails'), key: 'income' },
      { label: t('spendingDetails'), key: 'spending' },
    ],
    [t],
  );

  // The hook starts with '' until TabsToMobileSelect auto-selects the first tab
  const tabMode: CashFlowTabMode = TAB_ORDER.includes(
    selectedTabKey as CashFlowTabMode,
  )
    ? (selectedTabKey as CashFlowTabMode)
    : 'both';

  const swipeTouchStartX = useRef<number>(0);
  const swipeTouchStartY = useRef<number>(0);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null); // State for selected month
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Fetch categories to derive CashflowGroup when API omits it (matches CashFlowTable logic)
  const { data: categoriesData } = useGetCashflowCategoriesQuery({
    InvestorId: investorId,
  });

  const { incomeParentName, incomeSubcategorySet } = useMemo(() => {
    const parentMap: { [key: number]: string } = {};
    const subOptionsMap: { [key: string]: string[] } = {};
    if (categoriesData?.categories) {
      categoriesData.categories.forEach((cat) => {
        if (!cat.ParentId) {
          parentMap[cat.BankTransactionCategoryId] = cat.Name;
          subOptionsMap[cat.Name] = [];
        }
      });
      categoriesData.categories.forEach((cat) => {
        if (cat.ParentId && parentMap[cat.ParentId]) {
          subOptionsMap[parentMap[cat.ParentId]].push(cat.Name);
        }
      });
    }
    const parentNames = Object.keys(subOptionsMap);
    const incomeParent =
      parentNames.find((n) => n.toLowerCase() === 'income') || 'Income';
    const incomeSubs = new Set(subOptionsMap[incomeParent]);
    return { incomeParentName: incomeParent, incomeSubcategorySet: incomeSubs };
  }, [categoriesData]);

  // Normalize transactions: derive CashflowGroup from categories when API value is missing or not income/spending
  const chartTransactions = useMemo(() => {
    return transactions.map((t) => {
      const raw = normalizeCashflowGroup(t.CashflowGroup);
      if (raw === 'income' || raw === 'spending') {
        return t;
      }
      const parentName = (t.ParentCategoryName || '').toLowerCase();
      const catName = t.CategoryName || '';
      const derivedGroup =
        parentName === incomeParentName.toLowerCase() ||
        (catName && incomeSubcategorySet.has(catName))
          ? 'Income'
          : 'Spending';
      return { ...t, CashflowGroup: derivedGroup };
    });
  }, [transactions, incomeParentName, incomeSubcategorySet]);

  // Handler for month selection from the chart
  const handleBarClick = (month: string, category?: string) => {
    setSelectedMonth(month);
    setSelectedCategory(category || null);
    setViewMode(ViewMode.Table);
  };

  // Function to handle view mode change, clearing month selection
  const handleViewModeChange = (newMode: ViewMode) => {
    setSelectedMonth(null);
    setSelectedCategory(null);
    setViewMode(newMode);
  };

  // Function to clear the selected month
  const clearSelection = () => {
    setSelectedMonth(null);
    setSelectedCategory(null);
  };

  const availableViewModes = [ViewMode.Bar, ViewMode.Table];

  // Handler for tab changes
  const handleTabChange = (tab: TabItem) => {
    setSelectedTab(tab);
    setSelectedMonth(null);
    setSelectedCategory(null);
  };

  const handleChartTouchStart = (e: React.TouchEvent) => {
    swipeTouchStartX.current = e.touches[0].clientX;
    swipeTouchStartY.current = e.touches[0].clientY;
  };

  const handleChartTouchEnd = (e: React.TouchEvent) => {
    const deltaX = e.changedTouches[0].clientX - swipeTouchStartX.current;
    const deltaY = e.changedTouches[0].clientY - swipeTouchStartY.current;
    const SWIPE_THRESHOLD = 50;
    if (
      Math.abs(deltaX) < SWIPE_THRESHOLD ||
      Math.abs(deltaX) < Math.abs(deltaY)
    )
      return;
    const currentIndex = TAB_ORDER.indexOf(tabMode);
    const nextIndex =
      deltaX < 0
        ? Math.min(currentIndex + 1, TAB_ORDER.length - 1)
        : Math.max(currentIndex - 1, 0);
    if (nextIndex !== currentIndex) {
      handleTabChange(tabs[nextIndex]);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        padding: '0 24px 24px 24px',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '32px',
        alignSelf: 'stretch',
        height: '100%',
        flex: '1 1 auto',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '32px',
          alignSelf: 'stretch',
          alignItems: 'flex-start',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
            gap: 1,
            alignItems: 'flex-end',
          }}
          ref={setBulkEditContainer}
        >
          <ViewModeBar
            viewModes={availableViewModes}
            currentViewMode={viewMode}
            setViewMode={handleViewModeChange} // Use the wrapper function
          />
        </Box>

        {/* Conditional Rendering based on Drilldown or ViewMode */}
        {viewMode === ViewMode.Bar ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              width: '100%',
              alignSelf: 'stretch',
            }}
          >
            {/* Tabs for Chart View */}
            <Box>
              <TabsToMobileSelect
                selectedTabKey={selectedTabKey}
                tabs={tabs}
                handleTabChange={handleTabChange}
                instanceId={instanceId}
                ariaLabel={t('cashFlowDetails.tabsAria')}
              />

              <Divider />
            </Box>

            {/* Chart Component */}
            <Box
              sx={{ width: '100%', overflowX: 'auto' }}
              onTouchStart={handleChartTouchStart}
              onTouchEnd={handleChartTouchEnd}
            >
              <CashFlowDetailsBarChart
                ref={chartRef}
                transactions={chartTransactions}
                tabMode={tabMode}
                startDate={startDate}
                endDate={endDate}
                onMonthSelect={handleBarClick}
              />
            </Box>
          </Box>
        ) : viewMode === ViewMode.Table ? (
          <>
            {(selectedCategory || selectedMonth) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  onClick={() => {
                    setViewMode(ViewMode.Bar);
                    clearSelection();
                  }}
                  size='small'
                >
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant='h6'>
                  {selectedCategory
                    ? selectedCategory === 'Uncategorized'
                      ? t('uncategorized')
                      : selectedCategory
                    : formatChartDateAxis(selectedMonth)}
                  {selectedCategory && selectedMonth
                    ? ` - ${formatChartDateAxis(selectedMonth)}`
                    : ''}
                </Typography>
              </Box>
            )}
            <CashFlowTable
              transactions={transactions}
              selectedMonth={selectedMonth}
              selectedCategory={selectedCategory}
              bulkEditButtonContainer={bulkEditContainer}
              investorId={investorId}
            />
          </>
        ) : null}
      </Box>
    </Box>
  );
};

export default CashFlowDetails;
